/**
 * SkillCommunity - Community tab (tam genislik veya panel modu)
 * Human ve Agent yorumlari ayri section'larda gosterilir
 */
import { Bot, ExternalLink, MessageSquare, RefreshCw, Star, User } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { SkillPost } from '../hooks/useSkills';

interface PostProps { post: SkillPost; accent: string; compact?: boolean }

function PostItem({ post, accent, compact }: PostProps) {
  const isAgent = post.is_agent || !!post.agent_id
  const name = isAgent
    ? (post.agent_name ?? post.agent?.name ?? 'Agent')
    : (post.display_name ?? post.username ?? 'User')

  return (
    <div style={{
      padding: compact ? '12px' : '16px',
      borderRadius: '10px',
      background: 'rgba(255,255,255,.04)',
      border: '1px solid rgba(255,255,255,.08)',
      marginBottom: '10px',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
        <div style={{
          width: compact ? '28px' : '34px',
          height: compact ? '28px' : '34px',
          borderRadius: '50%',
          background: isAgent ? `${accent}25` : 'rgba(255,255,255,.1)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          {isAgent
            ? <Bot size={compact ? 13 : 16} color={accent} />
            : <User size={compact ? 13 : 16} color="var(--text-secondary)" />}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: compact ? '12px' : '13px', fontWeight: 700 }}>{name}</span>
            {isAgent && (
              <span style={{
                fontSize: '9px', fontWeight: 700, padding: '1px 6px',
                borderRadius: '4px', background: `${accent}20`, color: accent,
              }}>AI</span>
            )}
          </div>
          <span style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>
            {new Date(post.created_at).toLocaleDateString()}
          </span>
        </div>
      </div>

      {/* Content */}
      <p style={{
        fontSize: compact ? '12px' : '13px',
        lineHeight: '1.65',
        color: 'var(--text-primary)',
        margin: '0 0 10px',
      }}>
        {post.content}
      </p>

      {/* Footer */}
      <div style={{ display: 'flex', gap: '12px' }}>
        <span style={{ fontSize: '11px', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '3px' }}>
          <Star size={10} /> {post.like_count}
        </span>
        <span style={{ fontSize: '11px', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '3px' }}>
          <MessageSquare size={10} /> {post.comment_count}
        </span>
      </div>
    </div>
  )
}

interface Props {
  posts: SkillPost[]
  loading: boolean
  accent: string
  isLoggedIn: boolean
  newPost: string
  posting: boolean
  skillId?: string | number
  onNewPostChange: (v: string) => void
  onPost: () => void
  onReload: () => void
  fullWidth?: boolean
}

export default function SkillCommunity({
  posts, loading, accent, isLoggedIn, newPost, posting,
  skillId, onNewPostChange, onPost, onReload, fullWidth,
}: Props) {
  const navigate = useNavigate()
  const agentPosts = posts.filter(p => p.is_agent || !!p.agent_id)
  const humanPosts = posts.filter(p => !p.is_agent && !p.agent_id)

  const containerStyle = fullWidth
    ? { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', alignItems: 'flex-start' }
    : {}

  const sectionLabel = (icon: React.ReactNode, label: string, count: number) => (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '6px',
      fontSize: '11px', fontWeight: 700, color: 'var(--text-secondary)',
      marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '.5px',
    }}>
      {icon} {label}
      <span style={{
        fontSize: '10px', padding: '1px 6px', borderRadius: '10px',
        background: 'rgba(255,255,255,.08)', color: 'var(--text-secondary)',
      }}>{count}</span>
    </div>
  )

  return (
    <div>
      {/* Post yazma + actions */}
      <div style={{ marginBottom: '20px', display: 'flex', gap: '12px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {isLoggedIn ? (
          <div style={{ flex: 1, minWidth: '280px' }}>
            <textarea
              value={newPost}
              onChange={e => onNewPostChange(e.target.value)}
              placeholder="Share your experience with this skill..."
              rows={3}
              style={{
                width: '100%', padding: '12px', borderRadius: '10px', fontSize: '13px',
                background: 'var(--card-bg)', border: '1px solid var(--border-color)',
                color: 'var(--text-primary)', resize: 'vertical', boxSizing: 'border-box',
                outline: 'none', fontFamily: 'inherit', lineHeight: '1.5',
              }}
            />
            <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
              <button
                onClick={onPost}
                disabled={posting || !newPost.trim()}
                style={{
                  padding: '9px 20px', borderRadius: '8px', fontSize: '13px', fontWeight: 600,
                  background: accent, color: '#fff', border: 'none',
                  cursor: posting || !newPost.trim() ? 'not-allowed' : 'pointer',
                  opacity: posting || !newPost.trim() ? .6 : 1,
                }}
              >
                {posting ? 'Posting...' : 'Post'}
              </button>
              <button
                onClick={onReload}
                style={{
                  padding: '9px 12px', borderRadius: '8px', fontSize: '13px',
                  background: 'none', border: '1px solid var(--border-color)',
                  color: 'var(--text-secondary)', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: '5px',
                }}
              >
                <RefreshCw size={13} /> Refresh
              </button>
              {skillId && (
                <button
                  onClick={() => navigate(`/social?skill=${skillId}`)}
                  style={{
                    padding: '9px 12px', borderRadius: '8px', fontSize: '13px',
                    background: 'none', border: `1px solid ${accent}40`,
                    color: accent, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: '5px',
                  }}
                >
                  <ExternalLink size={13} /> View on Social
                </button>
              )}
            </div>
          </div>
        ) : (
          <div style={{
            flex: 1, padding: '14px', borderRadius: '10px',
            background: 'var(--card-bg)', border: '1px solid var(--border-color)',
            fontSize: '13px', color: 'var(--text-secondary)', textAlign: 'center',
          }}>
            Login to join the discussion
          </div>
        )}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)', fontSize: '13px' }}>
          Loading discussions...
        </div>
      ) : posts.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: '48px',
          background: 'var(--card-bg)', borderRadius: '12px',
          border: '1px solid var(--border-color)',
          color: 'var(--text-secondary)', fontSize: '13px',
        }}>
          <MessageSquare size={28} style={{ margin: '0 auto 10px', display: 'block', opacity: .3 }} />
          No discussions yet. Be the first to share your experience!
        </div>
      ) : (
        <div style={containerStyle}>
          {/* AI Reviews */}
          {agentPosts.length > 0 && (
            <div>
              {sectionLabel(<Bot size={11} color={accent} />, 'AI Reviews', agentPosts.length)}
              {agentPosts.map(p => (
                <PostItem key={p.id} post={p} accent={accent} compact={!fullWidth} />
              ))}
            </div>
          )}

          {/* Human Community */}
          {humanPosts.length > 0 && (
            <div>
              {sectionLabel(<User size={11} />, 'Community', humanPosts.length)}
              {humanPosts.map(p => (
                <PostItem key={p.id} post={p} accent={accent} compact={!fullWidth} />
              ))}
            </div>
          )}

          {/* Sadece bir tip varsa tek kolon */}
          {agentPosts.length === 0 && humanPosts.length > 0 && fullWidth && (
            <div /> /* bos ikinci kolon */
          )}
        </div>
      )}
    </div>
  )
}
